/**
 * workflowLoopTreeUtils.ts — Workflow scoped loop 占位树（仅 workflow 引用）
 *
 * 职责：stage running 且尚无 loop 内容时，为 AgentLoopView / Spinner 提供 startedAt。
 * 不修改 createEmptyTree 默认，避免影响 AgentSession /chat 显示。
 */
import { createEmptyTree, type SessionTree, type SessionStatus } from '../../types/tree';

/** scoped loop 是否仍在写入（结构帧可能早于 agent 结束）。 */
export function isWorkflowLoopTreeActive(tree?: SessionTree): boolean {
  return tree?.status === 'running' || tree?.status === 'connecting';
}

/** Workflow loop 运行占位：status running + startedAt（Spinner 门禁）。 */
export function createWorkflowRunningLoopTree(seedMs: number = Date.now()): SessionTree {
  const tree = createEmptyTree();
  tree.status = 'running';
  tree.meta.startedAt = seedMs;
  return tree;
}

/** stage-done 等结构关闭时，将仍 running 的 scoped loop 树收口（Spinner / done 摘要）。 */
export function finalizeWorkflowLoopTree(
  tree: SessionTree,
  terminalStatus: Extract<SessionStatus, 'done' | 'error'> = 'done',
): SessionTree {
  if (tree.status === 'done' || tree.status === 'error') {
    return tree;
  }

  const byId = { ...tree.byId };
  for (const [id, node] of Object.entries(byId)) {
    if (!node) continue;
    if (
      (node.kind === 'text' || node.kind === 'reasoning')
      && node.status === 'streaming'
    ) {
      byId[id] = { ...node, status: 'done' };
    }
    if (node.kind === 'tool_call' && node.status === 'running') {
      byId[id] = {
        ...node,
        status: terminalStatus === 'error' ? 'failed' : 'done',
      };
    }
  }

  return {
    ...tree,
    byId,
    activeRoundId: null,
    status: terminalStatus,
  };
}
